/**
 * DAS-41: 거래소 점검 일정 관리 카드 (마이페이지)
 * - 진행 중 / 예정 / 지난 점검 목록 표시
 * - 새 점검 등록 모달 (시작 시간 퀵버튼 + 지속시간 프리셋 + 종료 시간 미리보기)
 * - 항목 삭제 확인 모달
 */
import React from "react";
import dayjs from "dayjs";
import { listSchedules, createSchedule, updateSchedule, deleteSchedule } from "../../services/maintenanceApi";

const EXCHANGE_LABELS = {
    upbit: "Upbit",
    bithumb: "Bithumb",
    binance: "Binance",
    korbit: "Korbit",
};

const EXCHANGE_OPTIONS = [
    { value: "upbit", label: "업비트 (Upbit)" },
    { value: "bithumb", label: "빗썸 (Bithumb)" },
    { value: "binance", label: "바이낸스 (Binance)" },
    { value: "korbit", label: "코빗 (Korbit)" },
];

const DURATION_PRESETS = [
    { hours: 1, label: "1시간" },
    { hours: 3, label: "3시간" },
    { hours: 6, label: "6시간" },
    { hours: 12, label: "12시간" },
    { hours: 24, label: "24시간" },
];

const DEFAULT_DURATION_HOURS = 6;

// datetime-local value 포맷: YYYY-MM-DDTHH:mm
const formatLocalInput = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
};

const parseLocalInput = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return d;
};

// 시작 시간 퀵버튼: 현재 기준 상대값
const buildQuickStartOptions = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const tomorrowMidnight = new Date(
        tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 0, 0, 0, 0
    );
    const tomorrow2AM = new Date(
        tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 2, 0, 0, 0
    );
    const nowRounded = new Date(now);
    nowRounded.setSeconds(0, 0);

    return [
        { label: "지금", value: formatLocalInput(nowRounded) },
        { label: "내일 00:00", value: formatLocalInput(tomorrowMidnight) },
        { label: "내일 02:00", value: formatLocalInput(tomorrow2AM) },
    ];
};

const computeEndAt = (startValue, durationHours) => {
    const start = parseLocalInput(startValue);
    if (!start || !durationHours) return null;
    const end = new Date(start.getTime() + durationHours * 60 * 60 * 1000);
    return end;
};

const classifySchedule = (item, now) => {
    const start = new Date(item.start_at);
    const end = new Date(item.end_at);
    if (end <= now) return "past";
    if (start <= now) return "ongoing";
    return "upcoming";
};

class MaintenanceCard extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            schedules: [],
            loading: true,
            showCreateModal: false,
            editingId: null,
            deleteConfirmId: null,
            form: {
                exchange: "upbit",
                start_at: "",
                durationHours: DEFAULT_DURATION_HOURS,
                customEndAt: "",
                useCustomEnd: false,
                reason: "",
            },
            submitting: false,
            formError: "",
        };
    }

    componentDidMount() {
        this._isMounted = true;
        this.loadSchedules();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    loadSchedules = async () => {
        this.setState({ loading: true });
        try {
            const res = await listSchedules();
            if (!this._isMounted) return;
            this.setState({ schedules: Array.isArray(res.data) ? res.data : [], loading: false });
        } catch (_e) {
            if (!this._isMounted) return;
            this.setState({ loading: false });
        }
    };

    onOpenCreateModal = () => {
        this.setState({
            showCreateModal: true,
            editingId: null,
            formError: "",
            form: {
                exchange: "upbit",
                start_at: "",
                durationHours: DEFAULT_DURATION_HOURS,
                customEndAt: "",
                useCustomEnd: false,
                reason: "",
            },
        });
    };

    onOpenEditModal = (item) => {
        const start = new Date(item.start_at);
        const end = new Date(item.end_at);
        // DAS-50: 수정 모드에서는 항상 절대 종료시각 입력으로 연다.
        // - 사용자는 "지속 시간"이 아닌 "종료 시각"을 직접 조정하고 싶어함
        //   (예: 점검이 일찍 끝남 → 종료시간을 현재로 당기기)
        // - 기존 end 값을 customEndAt에 pre-fill 해서 클릭 한 번으로 수정 가능하게 함
        this.setState({
            showCreateModal: true,
            editingId: item.id,
            formError: "",
            form: {
                exchange: item.exchange,
                start_at: formatLocalInput(start),
                durationHours: DEFAULT_DURATION_HOURS,
                customEndAt: formatLocalInput(end),
                useCustomEnd: true,
                reason: item.reason || "",
            },
        });
    };

    onCloseCreateModal = () => {
        this.setState({ showCreateModal: false, editingId: null });
    };

    onFormChange = (e) => {
        const { name, value } = e.target;
        this.setState((prev) => ({ form: { ...prev.form, [name]: value }, formError: "" }));
    };

    onQuickStart = (value) => {
        this.setState((prev) => ({ form: { ...prev.form, start_at: value }, formError: "" }));
    };

    onSelectDuration = (hours) => {
        this.setState((prev) => ({
            form: { ...prev.form, durationHours: hours, useCustomEnd: false, customEndAt: "" },
            formError: "",
        }));
    };

    onToggleCustomEnd = () => {
        this.setState((prev) => ({
            form: {
                ...prev.form,
                useCustomEnd: !prev.form.useCustomEnd,
                customEndAt: prev.form.useCustomEnd ? "" : prev.form.customEndAt,
            },
            formError: "",
        }));
    };

    // DAS-50: 점검이 예상보다 일찍 끝났을 때 종료시각을 현재로 당기는 퀵버튼.
    // 직접 입력 모드를 자동 활성화하고 customEndAt을 현재 시각(분 단위)으로 세팅한다.
    onSetEndAtNow = () => {
        const now = new Date();
        now.setSeconds(0, 0);
        this.setState((prev) => ({
            form: {
                ...prev.form,
                useCustomEnd: true,
                customEndAt: formatLocalInput(now),
            },
            formError: "",
        }));
    };

    resolveEndAt = (form) => {
        if (form.useCustomEnd) {
            return parseLocalInput(form.customEndAt);
        }
        return computeEndAt(form.start_at, form.durationHours);
    };

    onSubmitCreate = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const { form, editingId } = this.state;
        const start = parseLocalInput(form.start_at);
        if (!start) {
            this.setState({ formError: "시작 시간을 선택해 주세요." });
            return;
        }
        const end = this.resolveEndAt(form);
        if (!end) {
            this.setState({ formError: "종료 시간을 지정해 주세요." });
            return;
        }
        if (end <= start) {
            this.setState({ formError: "종료 시간은 시작 시간보다 이후여야 합니다." });
            return;
        }

        const payload = {
            start_at: start.toISOString(),
            end_at: end.toISOString(),
            reason: form.reason,
        };
        this.setState({ submitting: true });
        try {
            if (editingId) {
                await updateSchedule(editingId, payload);
            } else {
                await createSchedule({ exchange: form.exchange, ...payload });
            }
            this.setState({ showCreateModal: false, editingId: null, submitting: false });
            this.loadSchedules();
            if (typeof this.props.onSchedulesChanged === 'function') {
                this.props.onSchedulesChanged();
            }
        } catch (_e) {
            this.setState({
                submitting: false,
                formError: editingId
                    ? "수정에 실패했습니다. 다시 시도해 주세요."
                    : "등록에 실패했습니다. 다시 시도해 주세요.",
            });
        }
    };

    onDeleteClick = (id) => {
        this.setState({ deleteConfirmId: id });
    };

    onCancelDelete = () => {
        this.setState({ deleteConfirmId: null });
    };

    onConfirmDelete = async () => {
        const { deleteConfirmId } = this.state;
        try {
            await deleteSchedule(deleteConfirmId);
            this.setState({ deleteConfirmId: null });
            this.loadSchedules();
            if (typeof this.props.onSchedulesChanged === 'function') {
                this.props.onSchedulesChanged();
            }
        } catch (_e) {
            this.setState({ deleteConfirmId: null });
        }
    };

    renderSummaryStats() {
        const { schedules, loading } = this.state;
        const now = new Date();

        const ongoingCount = schedules.filter((s) => {
            const start = new Date(s.start_at);
            const end = new Date(s.end_at);
            return start <= now && end > now;
        }).length;

        const totalCount = schedules.length;

        const pastSchedules = schedules
            .filter((s) => new Date(s.end_at) <= now)
            .sort((a, b) => new Date(b.end_at) - new Date(a.end_at));
        const lastMaintenance = pastSchedules.length > 0
            ? dayjs(pastSchedules[0].end_at).format("MM-DD HH:mm")
            : null;

        const stats = [
            {
                label: "현재 점검 중",
                value: String(ongoingCount),
                unit: "건",
                accent: ongoingCount > 0 ? "text-loss" : "text-content",
            },
            {
                label: "전체 등록",
                value: String(totalCount),
                unit: "건",
                accent: "text-content",
            },
            {
                label: "최근 점검",
                value: lastMaintenance || "없음",
                unit: "",
                accent: lastMaintenance ? "text-content-secondary" : "text-content-muted",
            },
        ];

        return (
            <div className="grid grid-cols-3 gap-3 mb-4">
                {stats.map((stat) => (
                    <div
                        key={stat.label}
                        className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card"
                    >
                        <p className="text-xs uppercase tracking-wider text-content-secondary">
                            {stat.label}
                        </p>
                        {loading ? (
                            <div className="mt-2 h-7 w-16 rounded bg-surface-hover animate-pulse" />
                        ) : (
                            <p className={`mt-1 text-2xl font-bold ${stat.accent}`}>
                                {stat.value}
                                {stat.unit && (
                                    <span className="ml-1 text-sm font-medium text-content-muted">
                                        {stat.unit}
                                    </span>
                                )}
                            </p>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    renderScheduleList() {
        const { schedules, deleteConfirmId } = this.state;
        const now = new Date();
        const groups = { ongoing: [], upcoming: [], past: [] };
        schedules.forEach((item) => {
            const bucket = classifySchedule(item, now);
            groups[bucket].push(item);
        });
        groups.upcoming.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
        groups.past.sort((a, b) => new Date(b.end_at) - new Date(a.end_at));

        const sectionConfig = [
            { key: "ongoing", label: "진행 중", badge: "bg-loss-subtle text-loss border-loss/30" },
            { key: "upcoming", label: "예정", badge: "bg-brand-subtle text-brand-light border-brand/30" },
            { key: "past", label: "지난 점검", badge: "bg-surface-hover text-content-muted border-surface-border" },
        ];

        const renderItem = (item) => {
            const bucket = classifySchedule(item, now);
            const badgeColors =
                bucket === "ongoing"
                    ? "bg-loss-subtle text-loss"
                    : bucket === "upcoming"
                    ? "bg-brand-subtle text-brand-light"
                    : "bg-surface-hover text-content-muted";
            return (
                <li
                    key={item.id}
                    className="flex items-start justify-between rounded-xl border border-surface-border bg-surface-elevated p-3"
                >
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold uppercase ${badgeColors}`}>
                                {EXCHANGE_LABELS[item.exchange] || item.exchange}
                            </span>
                            {bucket === "ongoing" && (
                                <span className="text-xs font-medium text-loss">점검 중</span>
                            )}
                            {bucket === "upcoming" && (() => {
                                const diffMs = new Date(item.start_at) - now;
                                const diffH = Math.floor(diffMs / (1000 * 60 * 60));
                                const diffM = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                                return (
                                    <span className="text-xs font-medium text-brand-light">
                                        {diffH > 0 ? `${diffH}시간 ${diffM}분 후` : `${diffM}분 후`}
                                    </span>
                                );
                            })()}
                            {bucket === "ongoing" && (() => {
                                const remainMs = new Date(item.end_at) - now;
                                const remainH = Math.floor(remainMs / (1000 * 60 * 60));
                                const remainM = Math.floor((remainMs % (1000 * 60 * 60)) / (1000 * 60));
                                return (
                                    <span className="text-xs text-content-muted">
                                        (종료까지 {remainH > 0 ? `${remainH}시간 ${remainM}분` : `${remainM}분`})
                                    </span>
                                );
                            })()}
                        </div>
                        <div className="mt-1 text-xs text-content-secondary">
                            {dayjs(item.start_at).format("YYYY-MM-DD HH:mm")}
                            <span className="mx-1 text-content-muted">~</span>
                            {dayjs(item.end_at).format("MM-DD HH:mm")}
                            <span className="ml-1 text-content-muted">(KST)</span>
                        </div>
                        {item.reason && (
                            <div className="mt-0.5 text-xs text-content-muted truncate">
                                {item.reason}
                            </div>
                        )}
                    </div>
                    {!deleteConfirmId && (
                        <div className="ml-3 flex flex-shrink-0 items-center gap-1">
                            {bucket !== "past" && (
                                <button
                                    type="button"
                                    aria-label="수정"
                                    onClick={() => this.onOpenEditModal(item)}
                                    className="rounded px-2 py-1 text-xs text-content-secondary hover:bg-surface-hover hover:text-content transition-colors"
                                >
                                    수정
                                </button>
                            )}
                            <button
                                type="button"
                                aria-label="삭제"
                                onClick={() => this.onDeleteClick(item.id)}
                                className="rounded px-2 py-1 text-xs text-loss hover:bg-loss/10 transition-colors"
                            >
                                삭제
                            </button>
                        </div>
                    )}
                </li>
            );
        };

        return (
            <div className="space-y-4">
                {sectionConfig.map(({ key, label, badge }) => {
                    const list = groups[key];
                    if (!list.length) return null;
                    return (
                        <div key={key}>
                            <div className="mb-2 flex items-center gap-2">
                                <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-semibold ${badge}`}>
                                    {label}
                                </span>
                                <span className="text-xs text-content-muted">{list.length}건</span>
                            </div>
                            <ul className="grid gap-2 sm:grid-cols-2">{list.map(renderItem)}</ul>
                        </div>
                    );
                })}
            </div>
        );
    }

    render() {
        const { schedules, loading, showCreateModal, editingId, deleteConfirmId, form, submitting, formError } = this.state;
        const isEditMode = Boolean(editingId);
        const quickStartOptions = showCreateModal ? buildQuickStartOptions() : [];
        const endPreview = showCreateModal ? this.resolveEndAt(form) : null;

        return (
            <>
                {/* 요약 통계 카드 */}
                {this.renderSummaryStats()}

                {/* 점검 일정 목록 카드 */}
                <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                    {/* 헤더 */}
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs uppercase tracking-wider text-content-secondary">
                                점검 일정
                            </p>
                            <h2 className="mt-0.5 text-base font-semibold text-content">거래소 점검 일정</h2>
                        </div>
                        {!showCreateModal && (
                            <button
                                type="button"
                                onClick={this.onOpenCreateModal}
                                className="rounded-lg bg-brand-subtle border border-brand/30 px-3 py-1.5 text-sm font-medium text-brand-light hover:bg-brand/30 transition-colors"
                            >
                                새 점검 등록
                            </button>
                        )}
                    </div>

                    {/* 목록 */}
                    <div className="mt-4">
                        {loading ? (
                            <div aria-busy="true" className="space-y-2">
                                <div className="h-14 w-full rounded-xl bg-surface-hover animate-pulse" />
                                <div className="h-14 w-full rounded-xl bg-surface-hover animate-pulse opacity-70" />
                            </div>
                        ) : schedules.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                                <p className="text-sm text-content-secondary">등록된 점검 일정이 없습니다</p>
                                <p className="mt-1 text-xs text-content-muted">
                                    거래소 점검 시 "새 점검 등록" 버튼으로 일정을 추가하세요
                                </p>
                            </div>
                        ) : (
                            this.renderScheduleList()
                        )}
                    </div>
                </div>

                {/* 새 점검 등록 모달 */}
                {showCreateModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4">
                        <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-surface-border bg-surface-card p-6 shadow-xl">
                            <h3 className="text-base font-semibold text-content">
                                {isEditMode ? "점검 일정 수정" : "새 점검 일정 등록"}
                            </h3>
                            <p className="mt-1 text-xs text-content-muted">
                                {isEditMode
                                    ? "시간과 사유를 수정할 수 있습니다. 거래소는 변경할 수 없습니다."
                                    : "거래소 공지 기준으로 등록해 주세요. 모든 시간은 한국 시간(KST)입니다."}
                            </p>
                            <form onSubmit={this.onSubmitCreate} className="mt-4 space-y-4">
                                {/* 거래소 */}
                                <div>
                                    <label
                                        htmlFor="maintenance-exchange"
                                        className="block text-sm font-medium text-content mb-1"
                                    >
                                        거래소
                                    </label>
                                    <select
                                        id="maintenance-exchange"
                                        name="exchange"
                                        value={form.exchange}
                                        onChange={this.onFormChange}
                                        aria-label="거래소"
                                        disabled={isEditMode}
                                        className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm text-content disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {EXCHANGE_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* 시작 시간 */}
                                <div>
                                    <label className="block text-sm font-medium text-content mb-1">
                                        시작 시간 <span className="text-content-muted font-normal">(KST)</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="start_at"
                                        value={form.start_at}
                                        onChange={this.onFormChange}
                                        className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm text-content"
                                    />
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {quickStartOptions.map((opt) => (
                                            <button
                                                key={opt.label}
                                                type="button"
                                                onClick={() => this.onQuickStart(opt.value)}
                                                className="rounded-full border border-surface-border bg-surface-hover px-3 py-1 text-xs text-content-secondary hover:bg-brand/10 hover:text-brand-light hover:border-brand/40 transition-colors"
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* 지속 시간 */}
                                <div>
                                    <label className="block text-sm font-medium text-content mb-1">
                                        지속 시간
                                    </label>
                                    <div className="flex flex-wrap gap-1.5">
                                        {DURATION_PRESETS.map((opt) => {
                                            const active = !form.useCustomEnd && form.durationHours === opt.hours;
                                            return (
                                                <button
                                                    key={opt.hours}
                                                    type="button"
                                                    onClick={() => this.onSelectDuration(opt.hours)}
                                                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors border ${
                                                        active
                                                            ? "bg-brand/20 text-brand-light border-brand/30"
                                                            : "bg-surface-hover text-content-secondary border-surface-border hover:border-brand/40 hover:text-brand-light"
                                                    }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                        <button
                                            type="button"
                                            onClick={this.onToggleCustomEnd}
                                            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors border ${
                                                form.useCustomEnd
                                                    ? "bg-brand/20 text-brand-light border-brand/30"
                                                    : "bg-surface-hover text-content-secondary border-surface-border hover:border-brand/40 hover:text-brand-light"
                                            }`}
                                        >
                                            직접 입력
                                        </button>
                                    </div>

                                    {form.useCustomEnd && (
                                        <>
                                            <input
                                                type="datetime-local"
                                                name="customEndAt"
                                                aria-label="종료 시간 직접 입력"
                                                value={form.customEndAt}
                                                onChange={this.onFormChange}
                                                className="mt-2 w-full rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm text-content"
                                            />
                                            {/* DAS-50: 점검이 일찍 끝났을 때 종료시각을 현재로 당기는 퀵버튼 */}
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                <button
                                                    type="button"
                                                    onClick={this.onSetEndAtNow}
                                                    aria-label="종료 시간을 지금으로 설정"
                                                    className="rounded-full border border-surface-border bg-surface-hover px-3 py-1 text-xs text-content-secondary hover:bg-brand/10 hover:text-brand-light hover:border-brand/40 transition-colors"
                                                >
                                                    지금 종료
                                                </button>
                                            </div>
                                        </>
                                    )}

                                    {/* 종료 시간 미리보기 */}
                                    <div className="mt-2 rounded-lg border border-dashed border-surface-border bg-surface-hover/50 px-3 py-2 text-xs">
                                        <span className="text-content-muted">종료 시간 </span>
                                        {endPreview ? (
                                            <span className="font-medium text-content">
                                                {dayjs(endPreview).format("YYYY-MM-DD HH:mm")} KST
                                            </span>
                                        ) : (
                                            <span className="text-content-muted">— 시작 시간을 먼저 선택해 주세요</span>
                                        )}
                                    </div>
                                </div>

                                {/* 사유 */}
                                <div>
                                    <label className="block text-sm font-medium text-content mb-1">
                                        사유 <span className="text-content-muted font-normal">(선택)</span>
                                    </label>
                                    <textarea
                                        name="reason"
                                        value={form.reason}
                                        onChange={this.onFormChange}
                                        rows={2}
                                        placeholder="예: 업비트 정기 서버 점검"
                                        className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm text-content resize-none placeholder:text-content-muted"
                                    />
                                </div>

                                {/* 오류 메시지 */}
                                {formError && (
                                    <div className="rounded-lg border border-loss/30 bg-loss-subtle px-3 py-2 text-xs text-loss">
                                        {formError}
                                    </div>
                                )}

                                <div className="flex justify-end gap-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={this.onCloseCreateModal}
                                        className="rounded-lg border border-surface-border px-4 py-2 text-sm text-content-secondary hover:bg-surface-hover transition-colors"
                                    >
                                        취소
                                    </button>
                                    <button
                                        type="button"
                                        onClick={this.onSubmitCreate}
                                        disabled={submitting}
                                        className="rounded-lg bg-brand-subtle border border-brand/30 px-4 py-2 text-sm font-medium text-brand-light hover:bg-brand/30 disabled:opacity-50 transition-colors"
                                    >
                                        {submitting
                                            ? isEditMode
                                                ? "저장 중..."
                                                : "등록 중..."
                                            : isEditMode
                                            ? "저장"
                                            : "등록"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* 삭제 확인 모달 */}
                {deleteConfirmId && (() => {
                    const targetSchedule = schedules.find((s) => s.id === deleteConfirmId);
                    return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4">
                        <div className="w-full max-w-sm rounded-2xl border border-surface-border bg-surface-card p-6 shadow-xl">
                            {targetSchedule && (
                                <div className="mb-3 rounded-lg bg-surface-elevated p-3 text-xs text-content-secondary space-y-1">
                                    <div><span className="font-medium text-content">거래소:</span> {EXCHANGE_LABELS[targetSchedule.exchange] || targetSchedule.exchange}</div>
                                    <div><span className="font-medium text-content">일정:</span> {dayjs(targetSchedule.start_at).format("YYYY-MM-DD HH:mm")} ~ {dayjs(targetSchedule.end_at).format("YYYY-MM-DD HH:mm")} (KST)</div>
                                </div>
                            )}
                            <p className="text-sm text-content">정말 삭제하시겠습니까?</p>
                            <div className="mt-4 flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={this.onCancelDelete}
                                    className="rounded-lg border border-surface-border px-4 py-2 text-sm text-content-secondary hover:bg-surface-hover transition-colors"
                                >
                                    취소
                                </button>
                                <button
                                    type="button"
                                    onClick={this.onConfirmDelete}
                                    className="rounded-lg bg-loss px-4 py-2 text-sm font-medium text-content-inverse hover:bg-loss/90 transition-colors"
                                >
                                    확인
                                </button>
                            </div>
                        </div>
                    </div>
                    );
                })()}
            </>
        );
    }
}

export default MaintenanceCard;
